import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/design-system/primitives";
import { useAuth } from "@/lib/auth/AuthProvider";
import {
  useDoseChangeHolds,
  useDoseChangeHoldPrescriptions,
  useResolveDoseChangeHold,
  type DoseChangeHold,
  type ResolveDoseChangeHold,
} from "@/lib/api/doseChangeHolds";
import type { AssignedClinician } from "@/types/clinician";
import { DoseChangeReviewSheet } from "./DoseChangeReviewSheet";
import {
  ClearDoseChangeRequest,
  WITHDRAW_REASON_LABEL,
} from "./ClearDoseChangeRequest";
import { AlreadyHandledDoseChange } from "./AlreadyHandledDoseChange";
import { NoDoseChangeAvailable } from "./NoDoseChangeAvailable";
import { DoseRequestMessage } from "./DoseRequestMessage";

type Props = {
  patientId: string;
  readOnly?: boolean;
  orderId?: string;
  prescriber?: AssignedClinician | null;
  /** Only requests still holding fills (the conversation chart drawer). */
  openOnly?: boolean;
};

export function DoseChangeHolds(props: Props) {
  const { session, loading } = useAuth();
  const query = useDoseChangeHolds(props.patientId, props.readOnly);
  if (loading || !session) return null;
  if (query.isError)
    return (
      <p role="alert" className="text-14 text-danger">
        Dose-change holds could not be checked.{" "}
        <button className="underline" onClick={() => void query.refetch()}>
          Retry
        </button>
      </p>
    );
  if (query.isPending)
    return (
      <p role="status" className="text-14 text-ink-muted">
        Checking dose-change holds…
      </p>
    );
  const data = query.data;
  // Repeat requests folded into an open one are shown under it, not as holds.
  const visible = (data?.items ?? []).filter(
    (h) => !h.resolution?.automatic && (!props.openOnly || h.status === "open"),
  );
  if (!data || !visible.length) return null;
  return (
    <section
      aria-label="Dose-change holds"
      className="border border-warning bg-warning-wash p-15 flex flex-col gap-12"
    >
      <h3 className="font-mono text-14 uppercase">Dose-change holds</h3>
      {visible.map((hold) => (
        <Hold
          key={`${session.user.id}:${hold.id}`}
          {...props}
          hold={hold}
          canResolve={!props.readOnly && data.can_resolve}
          canWithdraw={!props.readOnly && Boolean(data.can_withdraw)}
          others={data.items.filter((h) => !h.resolution?.automatic)}
          folded={data.items.filter(
            (h) => h.resolution?.automatic && h.resolution.duplicate_of === hold.id,
          )}
        />
      ))}
    </section>
  );
}
function Hold({
  hold,
  canResolve,
  canWithdraw = false,
  others = [],
  folded = [],
  ...props
}: Props & {
  hold: DoseChangeHold;
  canResolve: boolean;
  canWithdraw?: boolean;
  others?: DoseChangeHold[];
  folded?: DoseChangeHold[];
}) {
  const resolve = useResolveDoseChangeHold(props.patientId);
  const [editing, setEditing] = useState(false);
  const prescriptions = useDoseChangeHoldPrescriptions(
    props.patientId,
    editing,
  );
  const [reviewVersion, setReviewVersion] = useState(hold.resolution_version);
  const [decision, setDecision] = useState<ResolveDoseChangeHold["decision"]>(
    "updated_prescription",
  );
  const [medicationId, setMedicationId] = useState("");
  const [note, setNote] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const active = hold.status === "open";
  const owner =
    hold.assigned_prescriber_name ||
    (props.prescriber &&
    props.prescriber.clinician_id === hold.assigned_prescriber_id
      ? props.prescriber.display_name || props.prescriber.email
      : null);
  const canResolveHere =
    canResolve &&
    Boolean(props.orderId) &&
    Number.isInteger(hold.resolution_version);
  return (
    <article className="border-t border-line pt-12 flex flex-col gap-8">
      <p className="text-14 font-medium">
        {hold.product_id.replaceAll("-", " ")} ·{" "}
        {hold.status === "withdrawn"
          ? hold.resolution?.reason === "no_change_available"
            ? "closed · no dose change available"
            : hold.resolution?.reason === "not_a_dose_request"
              ? "closed · not a dose request"
              : "cleared"
          : hold.status.replaceAll("_", " ")}
      </p>
      {active && (
        <p className="text-14">
          {hold.reconciliation_required
            ? "Clinician review required. Fulfillment status needs reconciliation."
            : "Dose-change hold active pending clinician review."}
        </p>
      )}
      <p className="text-12">
        Prescriber:{" "}
        {owner ||
          (hold.assigned_prescriber_id ? "Assigned prescriber" : "Unassigned")}
      </p>
      {!owner && hold.assigned_prescriber_id && (
        <p className="text-12 break-all text-ink-muted">
          Prescriber reference: {hold.assigned_prescriber_id}
        </p>
      )}
      <DoseRequestMessage hold={hold} />
      <p className="text-12">
        {hold.source_channel.replaceAll("_", " ")} ·{" "}
        {new Date(hold.created_at).toLocaleString()}
      </p>
      {hold.source_channel === "healthie_chat" &&
        /^\d+$/.test(hold.conversation_id) &&
        !props.readOnly && (
          <Link
            className="underline text-14"
            to={`/clinician-portal/inbox/${hold.conversation_id}`}
          >
            Open source conversation
          </Link>
        )}
      {hold.reconciliation_required && (
        <p className="text-14">
          This request requires prescription reconciliation before release.
        </p>
      )}
      {folded.length > 0 && (
        <div className="flex flex-col gap-5">
          <p className="text-12">Also asked (folded into this request):</p>
          {folded.map((f) => (
            <blockquote
              key={f.id}
              className="text-14 whitespace-pre-wrap break-words border-l-2 border-line pl-8"
            >
              {f.source_message}
              <span className="block text-12 text-ink-muted">
                {f.source_channel.replaceAll("_", " ")} ·{" "}
                {new Date(f.created_at).toLocaleString()}
              </span>
            </blockquote>
          ))}
        </div>
      )}
      {hold.status === "withdrawn" && hold.resolved_at && (
        <p className="text-14">
          {hold.resolution?.reason && hold.resolution.reason !== "duplicate" ? "Closed" : "Cleared"}{" "}
          {new Date(hold.resolved_at).toLocaleString()}
          {hold.resolution?.reason
            ? ` · ${WITHDRAW_REASON_LABEL[hold.resolution.reason]}`
            : ""}
        </p>
      )}
      {hold.status !== "withdrawn" && hold.resolved_at && (
        <p className="text-14">
          Resolved {new Date(hold.resolved_at).toLocaleString()}
          {hold.resolution
            ? ` · ${(hold.resolution.decision ?? "").replaceAll("_", " ")}`
            : ""}
        </p>
      )}
      {hold.status === "resolved" && (
        <p className="text-14">
          The previous dose remains protected from reuse. Review and authorize
          each subsequent prescription explicitly.
        </p>
      )}
      {hold.resolution?.note && (
        <p className="text-14 whitespace-pre-wrap">{hold.resolution.note}</p>
      )}
      {canResolveHere && !editing && hold.status !== "withdrawn" && (
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            setReviewVersion(hold.resolution_version);
            setEditing(true);
          }}
        >
          {active ? "Resolve dose-change hold" : "Review next prescription"}
        </Button>
      )}
      {canResolve && active && !props.orderId && <ReviewToggle hold={hold} />}
      {canWithdraw && active && !editing && (
        <>
          <AlreadyHandledDoseChange hold={hold} />
          <NoDoseChangeAvailable
            key={`no-change:${hold.id}:${hold.resolution_version}`}
            hold={hold}
            folded={folded}
          />
          <ClearDoseChangeRequest hold={hold} others={others} />
        </>
      )}
      {editing && canResolveHere && (
        <form
          className="flex flex-col gap-10"
          onSubmit={(event) => {
            event.preventDefault();
            if (
              !confirmed ||
              !prescriptions.data?.items.some(
                (item) => item.id === medicationId,
              ) ||
              prescriptions.isError ||
              prescriptions.isFetching ||
              note.trim().length < 5 ||
              !props.orderId ||
              resolve.isPending
            )
              return;
            resolve.mutate(
              {
                requestId: hold.id,
                body: {
                  decision,
                  order_id: props.orderId,
                  medication_id: medicationId,
                  note: note.trim(),
                  confirmed: true,
                  expected_resolution_version: reviewVersion,
                },
              },
              { onSuccess: () => setEditing(false) },
            );
          }}
        >
          <p className="text-14">
            Complete any prescription change through the existing prescribing
            workflow first. Resolving this hold does not prescribe medication or
            change billing.
          </p>
          <label className="text-14 flex flex-col gap-5">
            Clinical decision
            <select
              value={decision}
              onChange={(event) =>
                setDecision(
                  event.target.value as ResolveDoseChangeHold["decision"],
                )
              }
              className="bg-raised border border-line p-10"
            >
              <option value="updated_prescription">
                Updated prescription recorded
              </option>
              <option value="continue_existing">
                Continue existing prescription after review
              </option>
            </select>
          </label>
          <label className="text-14 flex flex-col gap-5">
            Reviewed chart medication
            <select
              required
              value={medicationId}
              onChange={(event) => setMedicationId(event.target.value)}
              className="bg-raised border border-line p-10"
            >
              <option value="">Select the reviewed medication</option>
              {prescriptions.data?.items.map((medication) => (
                <option key={medication.id} value={medication.id}>
                  {medication.name}
                </option>
              ))}
            </select>
          </label>
          {prescriptions.isFetching && (
            <p role="status" className="text-14">
              Checking active prescriptions…
            </p>
          )}
          {prescriptions.isError && (
            <p role="alert" className="text-14 text-danger">
              Active prescriptions could not be checked.{" "}
              <button
                type="button"
                className="underline"
                onClick={() => void prescriptions.refetch()}
              >
                Retry prescription lookup
              </button>
            </p>
          )}
          {!prescriptions.isFetching &&
            !prescriptions.isError &&
            prescriptions.data?.items.length === 0 && (
              <p className="text-14">
                No active prescription is available to verify. Complete the
                existing prescribing workflow, then refresh this list.
              </p>
            )}
          <button
            type="button"
            className="text-14 underline self-start"
            onClick={() => void prescriptions.refetch()}
          >
            Refresh prescriptions
          </button>
          <label className="text-14 flex flex-col gap-5">
            Clinical resolution note
            <textarea
              required
              minLength={5}
              maxLength={2000}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              className="bg-raised border border-line p-10"
            />
          </label>
          <label className="text-14 flex gap-8">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            I reviewed this request and the selected prescription. Release this
            dose-change hold; other holds and clinical and pharmacy checks still
            apply.
          </label>
          <div className="flex gap-10">
            <Button
              type="submit"
              disabled={
                !confirmed ||
                !prescriptions.data?.items.some(
                  (item) => item.id === medicationId,
                ) ||
                prescriptions.isError ||
                prescriptions.isFetching ||
                note.trim().length < 5 ||
                resolve.isPending
              }
            >
              Confirm resolution
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={resolve.isPending}
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
          </div>
          {resolve.isError && (
            <p role="alert" className="text-14 text-danger">
              {resolve.error instanceof Error
                ? resolve.error.message
                : "The hold could not be resolved. Refresh and retry."}
            </p>
          )}
        </form>
      )}
    </article>
  );
}

function ReviewToggle({ hold }: { hold: DoseChangeHold }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-8">
      <Button
        type="button"
        variant="secondary"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {open ? "Close review" : "Review this request"}
      </Button>
      {open && <DoseChangeReviewSheet hold={hold} />}
    </div>
  );
}
